import { useEffect, useState } from "react";
import { CheckCheck, Eye, EyeOff, Trash2, Star } from "lucide-react";
import { api } from "@/lib/api";
import { Panel } from "@/components/admin/shared";
import { GoogleReviewForm } from "@/components/admin/GoogleReviewForm";
import { waLink } from "@/lib/api";

const when = (iso) => new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });

export const NotificationsTab = () => {
  const [items, setItems] = useState([]);
  const [reqs, setReqs] = useState([]);
  const load = () => {
    api.get("/admin/notifications").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/service-requests").then((r) => setReqs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const readAll = async () => { await api.post("/admin/notifications/read-all"); load(); window.dispatchEvent(new Event("lg-refresh-stats")); };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Panel title="Avisos del taller" action={<button className="btn-ghost !py-2" onClick={readAll} data-testid="notif-read-all-btn"><CheckCheck size={16} /> Marcar leídos</button>}>
        <ul className="space-y-2" data-testid="notifications-list">
          {items.map((n) => (
            <li key={n.id} className={`rounded-sm border p-3 text-sm ${n.read ? "border-line" : "border-gold/50 bg-gold/5"}`}>
              <div className="flex justify-between gap-2"><span className="font-semibold">{n.title}</span><span className="text-xs text-mute2">{when(n.created_at)}</span></div>
              <p className="mt-1 text-mute">{n.message}</p>
            </li>
          ))}
          {!items.length && <li className="text-mute">Sin avisos.</li>}
        </ul>
      </Panel>
      <Panel title="Cotizaciones y solicitudes">
        <ul className="space-y-2" data-testid="service-requests-list">
          {reqs.map((r) => (
            <li key={r.id} className="rounded-sm border border-line p-3 text-sm">
              <div className="flex justify-between gap-2"><span className="font-semibold">{r.name} · {r.phone}</span><span className="text-xs text-mute2">{when(r.created_at)}</span></div>
              <p className="text-mute">{r.vehicle}</p><p className="mt-1">{r.service && <b>{r.service}: </b>}{r.request}</p>
              <a href={waLink(r.phone, `Hola ${r.name}, te escribimos del Taller La Gringa sobre tu solicitud de ${(r.service || "servicio").toLowerCase()} para tu ${r.vehicle}.`)} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs font-semibold text-green underline" data-testid={`request-reply-${r.id}`}>Responder por WhatsApp</a>
            </li>
          ))}
          {!reqs.length && <li className="text-mute">Sin solicitudes de cotización.</li>}
        </ul>
      </Panel>
    </div>
  );
};

export const ReviewsTab = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/reviews").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (r) => { await api.patch(`/admin/reviews/${r.id}`, { approved: !r.approved }); load(); };
  const remove = async (r) => { if (window.confirm("¿Eliminar opinión?")) { await api.delete(`/admin/reviews/${r.id}`); load(); } };
  const feature = async (r) => { await api.patch(`/admin/reviews/${r.id}`, { featured: !r.featured }); load(); };
  return (
    <div className="grid gap-6 lg:grid-cols-[380px,1fr]">
    <GoogleReviewForm onAdded={load} />
    <Panel title="Opiniones">
      <ul className="space-y-3" data-testid="admin-reviews-list">
        {items.map((r) => (
          <li key={r.id} className="flex flex-col sm:flex-row justify-between gap-3 rounded-sm border border-line p-4 text-sm">
            <div>
              <p className="flex items-center gap-2 font-semibold">{r.name} <span className="flex text-gold">{[...Array(r.rating)].map((_, i) => <Star key={i} size={13} fill="currentColor" />)}</span>{r.source === "google" && <span className="rounded-full bg-surface px-2 text-[10px] text-mute">Google</span>}</p>
              <p className="mt-1 text-ink2">{r.comment}</p>
              <p className={`mt-1 text-xs ${r.approved ? "text-emerald-700" : "text-yellow-700"}`}>{r.approved ? "Publicada" : "Pendiente de aprobación"}{r.featured && " · Destacada en inicio"}</p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button className="btn-ghost !px-3 !py-2" onClick={() => feature(r)} data-testid={`review-feature-${r.id}`}><Star size={15} /> {r.featured ? "Quitar destacada" : "Destacar"}</button>
              <button className="btn-ghost !px-3 !py-2" onClick={() => toggle(r)} data-testid={`review-toggle-${r.id}`}>{r.approved ? <><EyeOff size={15} /> Ocultar</> : <><Eye size={15} /> Publicar</>}</button>
              <button className="btn-ghost !px-3 !py-2 !text-red-400" onClick={() => remove(r)} aria-label="Eliminar" data-testid={`review-delete-${r.id}`}><Trash2 size={15} /></button>
            </div>
          </li>
        ))}
        {!items.length && <li className="text-mute">Aún no hay opiniones.</li>}
      </ul>
    </Panel>
    </div>
  );
};